// Zweck: Gemeinsame Geräteoberfläche für OpsApp und isolierte lokale Simulation. Stand: 21.10.2026.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { readWarehouseCode } from '../services/readWarehouseCode';
import { warehouseCodeMatches } from '../services/warehouseCodes';
import WarehouseProjectPicker from '../components/WarehouseProjectPicker';
import WarehouseQuantity from '../components/WarehouseQuantity';
import WarehouseDocumentPicker from '../components/WarehouseDocumentPicker';
import WarehouseDeviceTestPanel from '../components/WarehouseDeviceTestPanel';
import WarehouseStockArticleCard from '../components/WarehouseStockArticleCard';
import WarehouseDeviceCard from '../components/WarehouseDeviceCard';
import WarehouseDeviceReview from '../components/WarehouseDeviceReview';
import {
  WarehouseHomeActions,
  WarehouseIcon,
  WarehouseSteps,
} from '../components/WarehousePresentation';
import {
  availableDevice,
  emptySim,
  simStore,
  sendSimulationQueue,
  captureStarted,
  canStopSimulation,
  stopSimulation,
  simMatches,
  simulationStock,
  withDeviceSnapshot,
  resolvedSimulationRequests,
} from '../services/deviceSimulation';
import type {
  SimLocal,
  SimOperation,
  SimDevice,
  SimQueueEntry,
  SimEvent,
  SimCapture,
} from '../interfaces/deviceSimulation';
import { warehouseAccessStatus } from '../services/warehouseQueue';
import type {
  DeviceTransport,
  DeviceSearchFilter,
  DeviceTestControls,
} from '../interfaces/deviceSimulation';
import './warehouse.css';
import './warehouseDevicesTest.css';
const titles: Record<string, string> = {
  receipt: 'Wareneingang',
  issue: 'Material ausgeben',
  return: 'Material zurücknehmen',
  block: 'Gerät sperren',
  release: 'Gerät freigeben',
  serial: 'Seriennummer berichtigen',
  note: 'Notiz ändern',
  legacy_return: 'Rückgabe aus Altbestand',
  opening_units: 'Bestand auf Einzelgeräte umstellen',
  correction: 'Buchung zurücknehmen',
};
const conditions: Record<string, string> = {
  ok: 'In Ordnung',
  damaged: 'Beschädigt',
  inspection_required: 'Prüfung nötig',
};
const inputDevice = (d: SimDevice) => ({
  id: d.id,
  articleId: d.articleId,
  version: d.version,
  serial: d.serial,
  label: d.label,
  condition: d.condition,
  note: d.note,
  physicalConfirmed: false,
});
export default function WarehouseDevicesScreen({
  api: transport,
  storageKey: key,
  test,
}: {
  api: DeviceTransport;
  storageKey: string;
  test?: DeviceTestControls;
}) {
  const api: DeviceTransport = {
    syncHero: transport.syncHero,
    state: () => checkAccess(transport.state()),
    status: (id) => checkAccess(transport.status(id)),
    book: (operation, lose) => checkAccess(transport.book(operation, lose)),
    search: (query, filter) => checkAccess(transport.search(query, filter)),
    history: (id) => checkAccess(transport.history(id)),
  };
  const [accessDenied, setAccessDenied] = useState<401 | 403>();
  const inFlight = useRef<Promise<void>>(Promise.resolve());
  const [data, setData] = useState<SimLocal>(emptySim);
  const ref = useRef(data);
  const working = useRef(data);
  const writes = useRef(Promise.resolve());
  const failedWrite = useRef<SimLocal | null>(null);
  const [storageError, setStorageError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [checked, setChecked] = useState(false);
  const [damage, setDamage] = useState(false);
  const [ready, setReady] = useState(false),
    [locked, setLocked] = useState(false),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const [offline, setOffline] = useState(false),
    [connected, setConnected] = useState(navigator.onLine),
    [lose, setLose] = useState(false);
  const [view, setView] = useState<
    'home' | 'draft' | 'review' | 'devices' | 'history'
  >('home');
  const [step, setStep] = useState(1);
  const [stockLimit, setStockLimit] = useState(20);
  const [stockArticleId, setStockArticleId] = useState<string | null>(null);
  const [ledgerLimit, setLedgerLimit] = useState(20);
  const [adding, setAdding] = useState<'closed' | 'article' | 'device'>(
    'closed',
  );
  const [locationId, setLocationId] = useState('');
  const [query, setQuery] = useState(''),
    [serial, setSerial] = useState(''),
    [condition, setCondition] = useState('ok'),
    [note, setNote] = useState('');
  const [missing, setMissing] = useState(false),
    [physical, setPhysical] = useState(false),
    [deviceId, setDeviceId] = useState<string>(() => crypto.randomUUID());
  const [searchResults, setSearchResults] = useState<SimDevice[] | null>(null);
  const [selectionResults, setSelectionResults] = useState<SimDevice[] | null>(
    null,
  );
  const [articleId, setArticleId] = useState(''),
    [events, setEvents] = useState<SimEvent[]>([]),
    [historyTitle, setHistoryTitle] = useState('');
  const owns = useRef(false);
  const sending = useRef(false);
  const online = useRef(true);
  online.current = !offline && connected;
  const snapshot = data.snapshot,
    draft = data.draft;
  const metadata =
    !!draft && ['block', 'release', 'serial', 'note'].includes(draft.kind);
  const resolvedRequests = resolvedSimulationRequests(data);
  const unresolvedDamage = data.queue.filter(
    (q) =>
      q.operation.kind === 'block' &&
      ['pending', 'unknown', 'rejected'].includes(q.state) &&
      !resolvedRequests.has(q.operation.requestId),
  );
  const articlesById = useMemo(
    () => new Map(snapshot?.articles.map((a) => [a.id, a])),
    [snapshot?.articles],
  );
  const projectsById = useMemo(
    () => new Map(snapshot?.projects.map((p) => [p.id, p])),
    [snapshot?.projects],
  );
  const locationsById = useMemo(
    () => new Map(snapshot?.locations.map((l) => [l.id, l])),
    [snapshot?.locations],
  );
  const heroByArticle = useMemo(
    () => new Map(snapshot?.hero.map((h) => [h.articleId, h])),
    [snapshot?.hero],
  );
  const stocksByArticle = useMemo(() => {
    const index = new Map<
      string,
      NonNullable<SimLocal['snapshot']>['stocks']
    >();
    for (const stock of snapshot?.stocks ?? []) {
      const entries = index.get(stock.articleId) ?? [];
      entries.push(stock);
      index.set(stock.articleId, entries);
    }
    return index;
  }, [snapshot?.stocks]);
  const article = (id: string) => articlesById.get(id);
  const searchContext = useRef('');
  searchContext.current = JSON.stringify([
    key,
    view,
    adding,
    query,
    articleId,
    stockArticleId,
    draft?.kind,
    draft?.locationId,
    draft?.projectId,
  ]);
  const searchSequence = useRef(0);
  useEffect(() => {
    setStockLimit(20);
  }, [query]);
  const locationName = (id: string | null) =>
    locationsById.get(id ?? '')?.name ?? id ?? 'Kein Lagerort';
  const articleCode = (value: string) =>
    snapshot?.articles.find((a) => warehouseCodeMatches(a, value));
  const label = 'TMP-' + deviceId.toUpperCase();
  const eligible = (d: SimDevice) =>
    draft?.kind === 'issue'
      ? availableDevice(d) && d.locationId === draft.locationId
      : draft?.kind === 'return'
        ? d.state === 'issued' && d.projectId === draft.projectId
        : true;
  // Felder reagieren sofort; Buchungs- und Übernahmeaktionen werden erst nach dauerhaftem Schreiben bestätigt.
  async function save(
    next: SimLocal | ((current: SimLocal) => SimLocal),
    optimistic = false,
  ) {
    const value = structuredClone(
      typeof next === 'function' ? next(working.current) : next,
    );
    working.current = value;
    if (optimistic) setData(value);
    setSaving(true);
    const task = writes.current
      .catch(() => undefined)
      .then(async () => {
        try {
          await simStore.setItem(key, value);
          ref.current = value;
          if (working.current === value) setData(value);
          failedWrite.current = null;
          setStorageError(false);
        } catch {
          failedWrite.current = working.current;
          setStorageError(true);
          setError(
            'Nicht gespeichert. Ansicht offen lassen und erneut versuchen.',
          );
          throw new Error('Lokaler Speicher nicht verfügbar.');
        }
      });
    writes.current = task;
    try {
      await task;
    } finally {
      if (writes.current === task) setSaving(false);
    }
  }
  const captureValue = (): SimCapture => ({
    kind: damage ? 'damage' : 'new',
    articleId,
    deviceId,
    serial,
    condition,
    note,
    missing,
    physical,
  });
  function showCapture(c: SimCapture) {
    setArticleId(c.articleId);
    setDeviceId(c.deviceId);
    setSerial(c.serial);
    setCondition(c.condition);
    setNote(c.note);
    setMissing(c.missing);
    setPhysical(c.physical);
    setDamage(c.kind === 'damage');
    setAdding('device');
  }
  function updateCapture(patch: Partial<SimCapture>) {
    const c = { ...captureValue(), ...patch };
    showCapture(c);
    void save((current) => ({ ...current, capture: c })).catch(() => undefined);
  }
  function focusProblem(id: string) {
    requestAnimationFrame(() => {
      const element = document.getElementById(id);
      element?.scrollIntoView({ block: 'center', behavior: 'smooth' });
      element?.focus({ preventScroll: true });
    });
  }
  const localBlock = (id: string) =>
    unresolvedDamage.some((q) => q.operation.devices.some((d) => d.id === id));
  function deviceProblem(d: SimOperation['devices'][number]) {
    if (!draft || metadata || !['issue', 'return'].includes(draft.kind))
      return '';
    if (draft.kind === 'issue' && localBlock(d.id))
      return 'Schadensmeldung noch nicht geklärt. Dieses Gerät nicht ausgeben.';
    if (draft.kind === 'return' && localBlock(d.id) && d.condition === 'ok')
      return 'Offene Schadensmeldung: Beschädigt oder Prüfung nötig wählen. Eine Rückgabe hebt die Sperre nicht auf.';
    const current = snapshot?.devices.find((x) => x.id === d.id);
    if (!current) return '';
    if (
      draft.kind === 'issue' &&
      (!availableDevice(current) || current.locationId !== draft.locationId)
    )
      return 'Gerät an diesem Lagerort nicht verwendbar. Anderes Gerät wählen oder entfernen.';
    if (
      draft.kind === 'return' &&
      (current.state !== 'issued' || current.projectId !== draft.projectId)
    )
      return `Gerät gehört zu Auftrag ${current.projectId ?? '–'}. Auftrag ändern oder Gerät entfernen.`;
    return '';
  }
  function quantityProblem() {
    for (const line of draft?.lines ?? []) {
      const a = article(line.articleId);
      if (a?.tracked && draft?.devices.some((d) => d.articleId === a.id))
        continue;
      if (a?.canOpenUnits && draft?.kind === 'opening_units') continue;
      if (!a || a.tracked)
        return 'Artikel nicht mehr als Mengenartikel verfügbar. Entfernen und neu auswählen.';
      if (
        !Number.isFinite(line.quantity) ||
        line.quantity <= 0 ||
        line.quantity > 999999999 ||
        Math.abs(line.quantity * 1000 - Math.round(line.quantity * 1000)) >
          0.0000001 ||
        (!a.fractional && !Number.isInteger(line.quantity))
      )
        return `Menge für ${a.name} eintragen: ${a.fractional ? 'Menge' : 'ganze Menge'} größer als 0.`;
      const stock =
        draft && simulationStock(data, line.articleId, draft.locationId);
      if (draft?.kind === 'issue' && stock != null && line.quantity > stock)
        return `${line.quantity} benötigt, nach diesem Stand ${Math.max(0, stock)} verfügbar. Menge ändern oder Bestand aktualisieren.`;
    }
    return '';
  }
  async function checkAccess<T>(task: Promise<T>): Promise<T> {
    try {
      return await task;
    } catch (e) {
      const status = warehouseAccessStatus(e);
      if (status && owns.current) {
        setAccessDenied(status);
        await save((current) => ({ ...current, accessDenied: status })).catch(
          () => undefined,
        );
      }
      throw e;
    }
  }
  async function stopQueued(id: string) {
    if (sending.current) {
      setError('Übertragung bereits gestartet. Ergebnis prüfen.');
      return;
    }
    sending.current = true;
    setBusy(true);
    try {
      await save((current) => stopSimulation(current, id));
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nicht gestoppt.');
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }
  async function reopenStopped(q: SimQueueEntry) {
    if (draft) {
      setError(
        'Zuerst die offene Liste fortsetzen. Der gestoppte Vorgang bleibt erhalten.',
      );
      return;
    }
    try {
      await save((current) => ({
        ...current,
        draft: {
          ...q.operation,
          requestId: crypto.randomUUID(),
          replacesId: q.operation.replacesId,
        },
        draftFromStoppedId: q.operation.requestId,
        capture: null,
      }));
      setView('draft');
      setStep(2);
      setAdding('closed');
      setChecked(false);
      setError('');
    } catch {
      /* Speicherhinweis bleibt sichtbar. */
    }
  }
  async function beginDamage(d: SimOperation['devices'][number]) {
    if (captureStarted(working.current.capture)) {
      setError('Zuerst die offene Erfassung fortsetzen oder verwerfen.');
      return;
    }
    const c: SimCapture = {
      kind: 'damage',
      deviceId: d.id,
      articleId: d.articleId,
      serial: d.serial ?? '',
      condition: 'damaged',
      note: '',
      missing: false,
      physical: false,
    };
    try {
      await save((current) => ({ ...current, capture: c }));
      showCapture(c);
      setError('');
    } catch {
      /* Vorhandene Liste bleibt erhalten. */
    }
  }
  async function reportDamage() {
    if (!draft || busy || storageError) return;
    setBusy(true);
    try {
      await writes.current;
      const d = ref.current.draft?.devices.find((x) => x.id === deviceId);
      if (!d) throw new Error('Gerät nicht mehr auf der Liste.');
      const op: SimOperation = {
        requestId: crypto.randomUUID(),
        kind: 'block',
        locationId: draft.locationId,
        projectId: draft.projectId,
        reason: '',
        lines: [],
        devices: [{ ...d, condition, note }],
      };
      await save((current) => ({
        ...current,
        capture: null,
        currentRequestId: op.requestId,
        draft: {
          ...current.draft!,
          devices: current.draft!.devices.filter((x) => x.id !== d.id),
        },
        queue: [
          ...current.queue,
          {
            operation: op,
            state: 'pending',
            attempted: false,
            createdAt: new Date().toISOString(),
          },
        ],
      }));
      setAdding('closed');
      setDamage(false);
      setError('');
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Schadensmeldung nicht gespeichert.',
      );
    } finally {
      setBusy(false);
    }
    if (!failedWrite.current) await sync();
  }
  async function refresh() {
    const confirmedBeforeRead = new Set(
      ref.current.queue
        .filter((q) => q.state === 'confirmed')
        .map((q) => q.operation.requestId),
    );
    const fresh = await api.state();
    if (owns.current) setAccessDenied(undefined);
    if (!owns.current) return;
    await save((current) =>
      withDeviceSnapshot(current, fresh, confirmedBeforeRead),
    );
  }
  function sync() {
    if (sending.current) return inFlight.current;
    const task = performSync();
    inFlight.current = task;
    return task;
  }
  async function performSync() {
    if (
      !online.current ||
      !owns.current ||
      sending.current ||
      failedWrite.current
    )
      return;
    sending.current = true;
    setBusy(true);
    setError('');
    try {
      await writes.current;
      await api.state();
      await sendSimulationQueue(
        ref.current,
        api,
        save,
        () => online.current && owns.current,
        lose,
      );
      setLose(false);
      await refresh();
    } catch (e) {
      setLose(false);
      setError(
        e instanceof Error ? e.message : 'Status unklar. Erneut prüfen.',
      );
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }
  async function recoverStorage() {
    const failed = failedWrite.current;
    if (!failed || busy) return;
    setBusy(true);
    try {
      await save(failed);
      setError('');
      if (!failed.capture) {
        setAdding('closed');
        setDamage(false);
      }
      if (!failed.draft && failed.currentRequestId) setView('history');
      await sync();
    } catch {
      /* Eingaben und erneute Speichermöglichkeit bleiben erhalten. */
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    const changed = () => setConnected(navigator.onLine);
    window.addEventListener('online', changed);
    window.addEventListener('offline', changed);
    return () => {
      window.removeEventListener('online', changed);
      window.removeEventListener('offline', changed);
    };
  }, []);
  useEffect(() => {
    let disposed = false;
    let release = () => {};
    const hold = new Promise<void>((r) => {
      release = r;
    });
    setReady(false);
    setError('');
    setLocked(false);
    failedWrite.current = null;
    setStorageError(false);
    ref.current = emptySim();
    working.current = ref.current;
    setData(ref.current);
    if (!navigator.locks) {
      setError(
        'Bitte einen aktuellen Browser über localhost oder HTTPS verwenden.',
      );
      return;
    }
    void navigator.locks.request(key, { ifAvailable: true }, async (lock) => {
      if (disposed) return;
      if (!lock) {
        setLocked(true);
        return;
      }
      owns.current = true;
      try {
        const stored = await simStore.getItem<SimLocal>(key);
        if (disposed) return;
        ref.current = stored ?? emptySim();
        working.current = ref.current;
        setData(ref.current);
        setAccessDenied(ref.current.accessDenied);
        if (navigator.onLine) await refresh();
        if (!ref.current.snapshot)
          throw new Error(
            'Bitte einmal mit Verbindung öffnen, damit der Gerätebestand auf diesem Handy gespeichert wird.',
          );
        setReady(true);
      } catch (e) {
        if (ref.current.snapshot) setReady(true);
        else
          setError(
            e instanceof Error ? e.message : 'Gerätebereich nicht erreichbar.',
          );
      }
      await hold;
    });
    return () => {
      disposed = true;
      owns.current = false;
      void inFlight.current
        .catch(() => undefined)
        .then(() => writes.current.catch(() => undefined))
        .finally(release);
    };
    // Der Datenbereich und die Sperre wechseln gemeinsam mit dem simulierten Konto.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  useEffect(() => {
    if (ready && !offline && connected) void sync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, offline, connected]);
  useEffect(() => {
    if (snapshot && !snapshot.locations.some((l) => l.id === locationId))
      setLocationId(snapshot.locations[0]?.id ?? '');
  }, [snapshot, locationId]);
  async function edit(patch: Partial<SimOperation>) {
    if (!draft || busy || failedWrite.current) return false;
    try {
      await save(
        (current) => ({
          ...current,
          draft: { ...current.draft!, ...patch },
        }),
        true,
      );
      setError('');
      return true;
    } catch {
      setError(
        'Änderung nicht gespeichert. Ansicht offen lassen und erneut versuchen.',
      );
      return false;
    }
  }
  async function start(kind: string, unit?: SimDevice) {
    if (busy) return;
    if (ref.current.draft) {
      setError('Zuerst die offene Liste fortsetzen oder bewusst verwerfen.');
      return;
    }
    const op: SimOperation = {
      requestId: crypto.randomUUID(),
      kind,
      locationId: unit?.locationId ?? locationId,
      projectId: unit?.projectId ?? null,
      reason: '',
      devices: unit
        ? [
            {
              ...inputDevice(unit),
              condition: kind === 'block' ? 'damaged' : unit.condition,
            },
          ]
        : [],
      lines: [],
    };
    try {
      await save((current) => ({
        ...current,
        draft: op,
        capture: null,
        draftFromStoppedId: undefined,
      }));
    } catch {
      return;
    }
    setChecked(false);
    setDamage(false);
    setView('draft');
    setStep(kind === 'note' && !contextError(op) ? 2 : 1);
    setError('');
    setQuery('');
    setArticleId('');
    setAdding('closed');
    setEvents([]);
  }
  function contextError(op: SimOperation) {
    if (
      op.devices.length > 100 ||
      new Set([
        ...op.lines.map((l) => l.articleId),
        ...op.devices.map((d) => d.articleId),
      ]).size > 100
    )
      return 'Höchstens 100 Geräte und 100 Artikel je Vorgang. Bitte die Liste aufteilen.';
    if (
      op.reason.length > 500 ||
      op.devices.some(
        (d) => d.note.length > 500 || (d.serial?.length ?? 0) > 120,
      )
    )
      return 'Angaben kürzen: Seriennummer höchstens 120, Notiz und Begründung höchstens 500 Zeichen.';
    if (
      [
        'release',
        'serial',
        'correction',
        'legacy_return',
        'opening_units',
      ].includes(op.kind) &&
      !snapshot?.canManage
    )
      return 'Diese Änderung darf nur die Lagerkraft vornehmen.';
    if (!snapshot?.locations.some((l) => l.id === op.locationId))
      return 'Bitte einen verfügbaren Lagerort auswählen.';
    if (['issue', 'return', 'legacy_return'].includes(op.kind) && !op.projectId)
      return 'Bitte das Projekt auswählen.';
    if (
      [
        'release',
        'serial',
        'correction',
        'legacy_return',
        'opening_units',
      ].includes(op.kind) &&
      !op.reason.trim()
    )
      return 'Bitte eine kurze Begründung eingeben.';
    return '';
  }
  async function addNew() {
    if (!draft) return;
    if (!physical) {
      setError('Kennzeichnung am tatsächlichen Gerät bestätigen.');
      return;
    }
    const value = serial.trim();
    if (value.length > 120) {
      setError(
        'Seriennummer zu lang. Bitte den Code auf dem Typenschild prüfen (höchstens 120 Zeichen).',
      );
      return;
    }
    if (!missing && !value) {
      setError('Seriennummer fotografieren oder eingeben.');
      return;
    }
    if (!missing && articleCode(value)) {
      setError('Das ist ein Artikelcode. Seriennummer am Gerät scannen.');
      return;
    }
    if (
      !missing &&
      draft.devices.some((x) => x.articleId === articleId && x.serial === value)
    ) {
      setError('Dieses Gerät steht schon in der Liste.');
      return;
    }
    if (draft.devices.length >= 100) {
      setError('Zuerst diese Liste buchen. Höchstens 100 Geräte je Vorgang.');
      return;
    }
    setBusy(true);
    try {
      await writes.current;
      await save((current) => ({
        ...current,
        capture: null,
        draft: {
          ...current.draft!,
          devices: [
            ...draft.devices,
            {
              id: deviceId,
              articleId,
              version: 0,
              serial: missing ? null : value,
              label: missing ? label : null,
              condition: missing ? 'inspection_required' : condition,
              note,
              physicalConfirmed: true,
            },
          ],
        },
      }));
      setSerial('');
      setPhysical(false);
      setMissing(false);
      setNote('');
      setCondition('ok');
      setDeviceId(crypto.randomUUID());
    } catch {
      /* Eingaben bleiben bei Speicherfehler bestehen. */
    } finally {
      setBusy(false);
    }
  }
  async function addExisting(unit: SimDevice) {
    if (!draft) return;
    if (draft.devices.length >= 100) {
      setError('Zuerst diese Liste buchen. Höchstens 100 Geräte je Vorgang.');
      return;
    }
    if (draft.kind === 'issue' && localBlock(unit.id)) {
      setError(
        'Schadensmeldung noch nicht geklärt. Dieses Gerät nicht ausgeben.',
      );
      return;
    }
    if (working.current.draft?.devices.some((x) => x.id === unit.id)) {
      setError('Dieses Gerät steht schon in der Liste.');
      return;
    }
    if (
      ref.current.queue.some(
        (q) =>
          ['pending', 'unknown'].includes(q.state) &&
          q.operation.devices.some((x) => x.id === unit.id),
      )
    ) {
      setError(
        'Für dieses Gerät fehlt noch eine Bestätigung. Zuerst den offenen Vorgang prüfen.',
      );
      return;
    }
    const selected = inputDevice(unit);
    if (draft.kind === 'return' && localBlock(unit.id)) {
      const reports = unresolvedDamage
        .flatMap((q) => q.operation.devices)
        .filter((d) => d.id === unit.id);
      selected.condition = reports.some((d) => d.condition === 'damaged')
        ? 'damaged'
        : 'inspection_required';
    }
    try {
      await save((current) => ({
        ...current,
        snapshot: current.snapshot
          ? {
              ...current.snapshot,
              devices: [
                ...current.snapshot.devices.filter((d) => d.id !== unit.id),
                unit,
              ],
            }
          : null,
        draft: {
          ...current.draft!,
          devices: [...current.draft!.devices, selected],
        },
      }));
      setAdding('closed');
      setQuery('');
      setSelectionResults(null);
    } catch {
      /* Kein Hinzufügen ohne dauerhafte Speicherung. */
    }
  }
  async function searchDevices(selection = false) {
    if (!online.current) return;
    setBusy(true);
    setError('');
    const context = searchContext.current;
    const sequence = ++searchSequence.current;
    try {
      const filter: DeviceSearchFilter | undefined =
        selection && (draft?.kind === 'issue' || draft?.kind === 'return')
          ? {
              kind: draft.kind,
              ...(draft.kind === 'issue'
                ? { locationId: draft.locationId }
                : { projectId: draft.projectId ?? '' }),
              ...(adding === 'device' ? { articleId } : {}),
            }
          : !selection && stockArticleId
            ? { articleId: stockArticleId }
            : undefined;
      const found = await api.search(query, filter);
      if (
        !owns.current ||
        context !== searchContext.current ||
        sequence !== searchSequence.current
      )
        return;
      if (selection) setSelectionResults(found);
      else setSearchResults(found);
    } catch {
      if (
        owns.current &&
        context === searchContext.current &&
        sequence === searchSequence.current
      )
        setError('Suche benötigt Verbindung. Eingaben bleiben erhalten.');
    } finally {
      if (sequence === searchSequence.current) setBusy(false);
    }
  }
  async function chooseArticle(id: string) {
    if (!draft) return;
    if (captureStarted(working.current.capture)) {
      setError('Zuerst die offene Erfassung fortsetzen oder verwerfen.');
      setAdding('closed');
      return;
    }
    setError('');
    setQuery('');
    setSelectionResults(null);
    if (!article(id)?.tracked && draft.kind !== 'opening_units') {
      if (draft.lines.length >= 100) {
        setError('100 Artikel erreicht. Zuerst diese Liste buchen.');
        return;
      }
      if (draft.lines.some((l) => l.articleId === id)) {
        setError(
          'Dieser Artikel steht bereits auf der Liste. Dort die Menge ändern.',
        );
        setAdding('closed');
        return;
      }
      if (
        await edit({
          lines: [...draft.lines, { articleId: id, quantity: 1 }],
        })
      )
        setAdding('closed');
    } else {
      setDamage(false);
      setArticleId(id);
      setDeviceId(crypto.randomUUID());
      setSerial('');
      setMissing(false);
      setPhysical(false);
      setCondition('ok');
      setNote('');
      setAdding('device');
    }
  }
  async function scan(code: string) {
    code = code.trim();
    if (!code) {
      setError(
        'Bitte einen Code fotografieren oder einen Suchbegriff eingeben.',
      );
      return;
    }
    const isNew = ['receipt', 'legacy_return', 'opening_units'].includes(
      draft?.kind ?? '',
    );
    const matches =
      snapshot?.articles.filter((a) => warehouseCodeMatches(a, code)) ?? [];
    if (adding === 'article' && matches.length > 1) {
      setQuery(code);
      setError(
        'Dieser Code gehört zu mehreren Artikeln. Den richtigen Artikel auswählen.',
      );
      return;
    }
    const scannedArticle = matches[0];
    if (
      adding === 'article' &&
      scannedArticle &&
      (draft?.kind !== 'opening_units' || scannedArticle.canOpenUnits)
    ) {
      await chooseArticle(scannedArticle.id);
      return;
    }
    if (isNew) {
      if (adding !== 'device') {
        setError(
          'Zuerst den Artikel auswählen, danach die Seriennummer am Gerät scannen.',
        );
        return;
      }
      if (articleCode(code)) {
        setError(
          'Das ist ein Artikelcode. Bitte die Seriennummer am Gerät scannen.',
        );
        return;
      }
      updateCapture({ serial: code, physical: false });
      return;
    }
    const found =
      snapshot?.devices.filter((x) => x.serial === code || x.label === code) ??
      [];
    if (
      found.length === 1 &&
      eligible(found[0]) &&
      (adding !== 'device' || found[0].articleId === articleId)
    )
      await addExisting(found[0]);
    else {
      setQuery(code);
      setError(
        found.length
          ? 'Gerät ist für diesen Vorgang nicht verfügbar.'
          : 'Gerät nicht im geladenen Bestand. Online suchen.',
      );
    }
  }
  async function camera(file: File) {
    setBusy(true);
    try {
      const code = await readWarehouseCode(file);
      if (owns.current) await scan(code);
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'Code nicht erkannt. Bitte eingeben.',
      );
    } finally {
      setBusy(false);
    }
  }
  function complete(op: SimOperation): SimOperation {
    const quantities = new Map<string, number>();
    for (const d of op.devices)
      quantities.set(d.articleId, (quantities.get(d.articleId) ?? 0) + 1);
    const goods = op.lines.filter(
      (l) =>
        !snapshot?.articles.find((a) => a.id === l.articleId)?.tracked &&
        !article(l.articleId)?.canOpenUnits,
    );
    return {
      ...op,
      lines: metadata
        ? []
        : [
            ...Array.from(quantities, ([articleId, quantity]) => ({
              articleId,
              quantity,
            })),
            ...goods,
          ],
    };
  }
  async function review() {
    if (!draft || storageError) return;
    await writes.current.catch(() => undefined);
    if (failedWrite.current) return;
    setChecked(true);
    if (captureStarted(ref.current.capture)) {
      setError('Offene Erfassung zuerst abschließen oder bewusst verwerfen.');
      setAdding('closed');
      focusProblem('capture-resume');
      return;
    }
    const message = contextError(draft);
    if (message) {
      setError(message);
      setStep(1);
      focusProblem('context-error');
      return;
    }
    const quantityError = quantityProblem();
    if (quantityError) {
      setError(quantityError);
      focusProblem('qty-device-quantity-' + (draft?.lines[0]?.articleId ?? ''));
      return;
    }
    if (!metadata && !draft.devices.length && !draft.lines.length) {
      setError('Mindestens ein Gerät oder einen Artikel hinzufügen.');
      return;
    }
    for (const d of draft.devices) {
      const problem = deviceProblem(d);
      if (
        problem ||
        ((!metadata || draft.kind === 'serial') && !d.physicalConfirmed)
      ) {
        setError(
          problem ||
            `${d.serial ?? d.label}: Kennzeichnung am tatsächlichen Gerät noch abgleichen.`,
        );
        focusProblem('device-' + d.id);
        return;
      }
    }
    if (await edit(complete(draft))) {
      setView('review');
      setError('');
    }
  }
  async function submit() {
    if (!draft || busy || failedWrite.current) return;
    setBusy(true);
    try {
      await writes.current;
      const op = complete(ref.current.draft!);
      const message = contextError(op);
      if (message) throw new Error(message);
      if (
        ref.current.queue.some(
          (q) =>
            ['pending', 'unknown'].includes(q.state) &&
            q.operation.devices.some((x) =>
              op.devices.some((d) => d.id === x.id),
            ),
        )
      )
        throw new Error('Für ein Gerät fehlt noch eine Bestätigung.');
      await save({
        ...ref.current,
        draft: null,
        capture: null,
        currentRequestId: op.requestId,
        queue: [
          ...ref.current.queue,
          {
            operation: op,
            state: 'pending',
            attempted: false,
            createdAt: new Date().toISOString(),
            sourceStoppedId: ref.current.draftFromStoppedId,
          },
        ],
        draftFromStoppedId: undefined,
      });
      setView('history');
      setError('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Nicht gespeichert.');
      // Ohne vorgemerkte Buchung nicht synchronisieren: Der Prüfhinweis muss stehen bleiben.
      return;
    } finally {
      setBusy(false);
    }
    if (!failedWrite.current) await sync();
  }
  async function repair(q: SimQueueEntry) {
    if (draft) {
      setError('Zuerst offene Liste bearbeiten.');
      return;
    }
    try {
      await refresh();
      const fresh = ref.current.snapshot;
      const op = {
        ...q.operation,
        requestId: crypto.randomUUID(),
        replacesId: q.operation.requestId,
        devices: q.operation.devices.map((d) => {
          const latest = fresh?.devices.find((x) => x.id === d.id);
          return latest
            ? {
                ...inputDevice(latest),
                physicalConfirmed: false,
                ...(q.operation.kind === 'block'
                  ? { condition: d.condition, note: d.note }
                  : {}),
                ...(q.operation.kind === 'serial' ? { serial: d.serial } : {}),
                ...(q.operation.kind === 'note' ? { note: d.note } : {}),
              }
            : d;
        }),
      };
      try {
        await save((current) => ({ ...current, draft: op, capture: null }));
      } catch {
        return;
      }
      setChecked(false);
      setDamage(false);
      setView('draft');
      setStep(2);
      setError(
        ['block', 'release', 'serial', 'note'].includes(q.operation.kind)
          ? 'Aktuellen Gerätestand geladen. Deine Änderung prüfen und erneut bestätigen.'
          : 'Betroffene Geräte ersetzen oder entfernen. Nichts aus diesem Vorgang wurde gebucht.',
      );
    } catch {
      setError(
        'Für die Berichtigung zuerst verbinden und aktuellen Stand laden.',
      );
    }
  }
  async function correct(q: SimQueueEntry) {
    if (draft) {
      setError('Zuerst offene Liste bearbeiten.');
      return;
    }
    if (!online.current) {
      setError('Für die Rücknahme zuerst verbinden und aktuellen Stand laden.');
      return;
    }
    try {
      await refresh();
      const units = q.operation.devices
        .map((d) => ref.current.snapshot?.devices.find((x) => x.id === d.id))
        .filter((d): d is SimDevice => !!d);
      await save({
        ...ref.current,
        draft: {
          ...q.operation,
          requestId: crypto.randomUUID(),
          kind: 'correction',
          correctsId: q.operation.requestId,
          replacesId: undefined,
          reason: '',
          lines: [],
          devices: units.map(inputDevice),
        },
      });
      setView('draft');
      setStep(1);
    } catch {
      setError('Rücknahme nicht vorbereitet. Verbindung und Speicher prüfen.');
    }
  }
  async function history(d: SimDevice) {
    try {
      setEvents(await api.history(d.id));
      setHistoryTitle(d.id);
    } catch {
      setError('Gerätehistorie benötigt Verbindung.');
    }
  }
  const ledger: SimQueueEntry[] = [
    ...data.queue.map((q) => {
      // Ältere lokale Ergebnisse können noch ohne Serverzeit gespeichert sein.
      const stored = snapshot?.bookings.find(
        (b) => b.requestId === q.operation.requestId && b.status === q.state,
      );
      return stored ? { ...q, result: stored } : q;
    }),
    ...(snapshot?.bookings ?? [])
      .filter(
        (b) => !data.queue.some((q) => q.operation.requestId === b.requestId),
      )
      .map((b) => ({ operation: b.booking, state: b.status, result: b })),
  ];
  const pending = data.queue.filter((q) =>
    ['pending', 'unknown'].includes(q.state),
  );
  const deviceText = (d: SimDevice) =>
    [
      d.serial,
      d.label,
      d.articleId,
      article(d.articleId)?.name,
      article(d.articleId)?.number,
      ...(article(d.articleId)?.codes ?? []),
      d.projectId,
      projectsById.get(d.projectId ?? '')?.name,
      d.locationId,
      locationName(d.locationId),
    ].join(' ');
  const matchingArticles = (snapshot?.articles ?? [])
    .filter(
      (a) =>
        (!draft ||
          (draft.kind === 'opening_units'
            ? a.canOpenUnits && !a.tracked
            : !a.canOpenUnits &&
              (draft.kind !== 'legacy_return' || a.tracked))) &&
        simMatches([a.name, a.number, ...a.codes].join(' '), query),
    )
    .sort(
      (a, b) =>
        Number(warehouseCodeMatches(b, query)) -
        Number(warehouseCodeMatches(a, query)),
    );
  const stockArticles = (snapshot?.articles ?? []).filter(
    (a) =>
      (!stockArticleId || a.id === stockArticleId) &&
      simMatches([a.name, a.number, ...a.codes].join(' '), query),
  );
  const stockDevices = (searchResults ?? snapshot?.devices ?? []).filter(
    (d) =>
      (!stockArticleId || d.articleId === stockArticleId) &&
      (searchResults !== null || simMatches(deviceText(d), query)),
  );
  const exactDeviceMatch = (d: SimDevice) =>
    !!query.trim() &&
    [d.serial, d.label].some(
      (value) =>
        value?.toLocaleLowerCase('de') === query.trim().toLocaleLowerCase('de'),
    );
  const rankDevices = (a: SimDevice, b: SimDevice) =>
    Number(exactDeviceMatch(b)) - Number(exactDeviceMatch(a));
  const positionCount = new Set([
    ...(draft?.devices.map((d) => d.articleId) ?? []),
    ...(draft?.lines.map((l) => l.articleId) ?? []),
  ]).size;
  return (
    <main className="warehouse wh-device-sim">
      <header className="wh-header">
        <button
          className="wh-text-button"
          aria-label="Zur Lagerübersicht"
          onClick={() => setView('home')}
        >
          <img src="/360volt-logo.png" alt="360Volt" width="116" />
        </button>
        <h1>{test ? 'Lager' : 'Geräte'}</h1>
        {!test && (
          <Link className="wh-back" to="/projects">
            Projekte
          </Link>
        )}
      </header>
      <p className="wh-status" role="status">
        {busy
          ? 'Wird verarbeitet …'
          : saving
            ? 'Wird gespeichert …'
            : offline || !connected
              ? 'Ohne Verbindung'
              : 'Netzverbindung vorhanden'}{' '}
        {test ? '· Testbetrieb' : '· Geräte und Seriennummern'}
      </p>
      {!test && (
        <Link
          className="wh-back wh-route-link"
          to="/lager"
          aria-disabled={busy || saving || storageError}
          onClick={(event) => {
            if (busy || saving || storageError) event.preventDefault();
          }}
        >
          Zur Lagerübersicht und Materiallisten
        </Link>
      )}
      {snapshot && !accessDenied && (
        <p className="wh-field-hint">
          {snapshot.canManage
            ? 'Lagerkraft · Geräte prüfen und freigeben'
            : 'Monteur · Freigabe durch die Lagerkraft'}
        </p>
      )}
      {data.draft && !storageError && !saving && (
        <p className="wh-field-hint" role="status">
          Entwurf gespeichert · noch nicht gebucht
        </p>
      )}
      {storageError && (
        <button disabled={busy} onClick={() => void recoverStorage()}>
          Speichern erneut versuchen
        </button>
      )}
      {error && (
        <p role="alert" className="wh-sim-alert">
          {error}
        </p>
      )}
      {accessDenied ? (
        <section role="alert">
          <h2>
            {accessDenied === 403
              ? 'Kein Zugriff auf das Lager'
              : 'Bitte erneut anmelden'}
          </h2>
          <p>
            Gespeicherte Vorgänge bleiben erhalten. Es wird nichts übertragen.
          </p>
          {accessDenied === 401 && <a href="/login">Zur Anmeldung</a>}
          <button
            disabled={!connected}
            onClick={() => void refresh().catch(() => undefined)}
          >
            Zugriff erneut prüfen
          </button>
        </section>
      ) : locked ? (
        <section>
          <h2>Lager ist schon geöffnet</h2>
          <p>Anderes Gerätefenster schließen und hier neu laden.</p>
          <button onClick={() => location.reload()}>Erneut prüfen</button>
        </section>
      ) : !ready ? (
        <section>
          <p>
            {error
              ? 'Gerätebereich noch nicht verfügbar. Gespeicherte Eingaben bleiben erhalten.'
              : 'Gerätebestand wird geladen …'}
          </p>
          {error && (
            <button onClick={() => location.reload()}>Erneut versuchen</button>
          )}
        </section>
      ) : (
        <fieldset disabled={busy || storageError}>
          <nav
            aria-label="Lagerbereiche"
            className="wh-nav"
            hidden={view === 'home'}
          >
            {(['home', 'devices', 'history'] as const).map((v) => (
              <button
                key={v}
                disabled={busy}
                aria-pressed={view === v}
                onClick={() => {
                  setView(v);
                  setStockArticleId(null);
                  setEvents([]);
                  setQuery('');
                  setSearchResults(null);
                }}
              >
                <WarehouseIcon
                  kind={
                    v === 'home'
                      ? 'home'
                      : v === 'devices'
                        ? 'stock'
                        : 'history'
                  }
                />
                {v === 'home'
                  ? 'Übersicht'
                  : v === 'devices'
                    ? 'Bestand'
                    : 'Buchungen'}
              </button>
            ))}
          </nav>
          {draft && view !== 'draft' && view !== 'review' && (
            <button
              className="wh-resume"
              onClick={() => {
                setView('draft');
                setAdding('closed');
                setStep(
                  draft.devices.length ||
                    draft.lines.length ||
                    captureStarted(data.capture)
                    ? 2
                    : 1,
                );
              }}
            >
              Offene Liste fortsetzen
            </button>
          )}
          {view === 'home' && (
            <>
              <label className="wh-location">
                In welchem Lager bist du?
                <select
                  value={locationId}
                  onChange={(e) => setLocationId(e.target.value)}
                >
                  {snapshot?.locations.map((l) => (
                    <option key={l.id} value={l.id}>
                      {l.name}
                    </option>
                  ))}
                </select>
              </label>
              {!snapshot?.locations.length && (
                <p role="alert">
                  Noch kein Lagerort eingerichtet. Bitte die Lagerkraft
                  informieren.
                </p>
              )}
              <h2>Was möchtest du machen?</h2>
              <WarehouseHomeActions
                disabled={busy || !!draft || !locationId}
                stockDisabled={busy}
                onStart={(kind) => void start(kind)}
                onStock={() => {
                  setQuery('');
                  setSearchResults(null);
                  setView('devices');
                }}
              />
              <button
                className="wh-wide"
                aria-label="Buchungen"
                onClick={() => setView('history')}
              >
                Buchungen ansehen
              </button>
              {!!pending.length && (
                <p className="wh-notice">
                  {pending.length} Vorgang/Vorgänge noch nicht bestätigt. Im
                  Buchungen prüfen.
                </p>
              )}
              {snapshot?.canManage && (
                <details>
                  <summary>Bestand einrichten</summary>
                  <div className="wh-start">
                    <button onClick={() => void start('legacy_return')}>
                      Rückgabe aus Altbestand
                    </button>
                    <button onClick={() => void start('opening_units')}>
                      Altbestand einzeln aufnehmen
                    </button>
                  </div>
                </details>
              )}
            </>
          )}
          {draft?.kind === 'release' &&
            (view === 'draft' || view === 'review') && (
              <section aria-labelledby="release-title">
                <h2 id="release-title">Gerät freigeben</h2>
                {draft.devices.map((d) => (
                  <div className="wh-context" key={d.id}>
                    <strong>
                      {
                        snapshot?.articles.find((a) => a.id === d.articleId)
                          ?.name
                      }
                    </strong>
                    <p>
                      {d.serial ? 'Seriennummer' : 'Vorläufige Kennzeichnung'}:{' '}
                      <strong>{d.serial ?? d.label}</strong>
                    </p>
                    <p>Aktueller Zustand: {conditions[d.condition]}</p>
                    {d.note && <p>{d.note}</p>}
                  </div>
                ))}
                <p>
                  Prüfe das tatsächliche Gerät. Mit der Freigabe wird die Sperre
                  aufgehoben und der Zustand auf „In Ordnung“ gesetzt. Lagerort
                  und Auftrag bleiben erhalten.
                </p>
                <label>
                  Begründung
                  <input
                    maxLength={500}
                    placeholder="Was wurde geprüft oder behoben?"
                    value={draft.reason}
                    onChange={(e) => void edit({ reason: e.target.value })}
                  />
                </label>
                <p className="wh-field-hint">
                  {offline || !connected
                    ? 'Ohne Verbindung: Die Freigabe wird bei Verbindung automatisch gesendet. Bis zur Serverbestätigung bleibt das Gerät gesperrt.'
                    : 'Das Gerät bleibt bis zur Serverbestätigung gesperrt.'}
                </p>
                <button
                  className="wh-primary wh-wide"
                  disabled={busy || saving || !snapshot?.canManage}
                  onClick={() => void submit()}
                >
                  {offline || !connected
                    ? 'Freigabe bei Verbindung vormerken'
                    : 'Geprüft und freigeben'}
                </button>
                <button
                  className="wh-back"
                  onClick={() =>
                    void save({ ...working.current, draft: null })
                      .then(() => {
                        setError('');
                        setView('devices');
                      })
                      .catch(() => undefined)
                  }
                >
                  Abbrechen
                </button>
              </section>
            )}
          {draft && draft.kind !== 'release' && view === 'draft' && (
            <section>
              <WarehouseSteps
                step={draft.kind === 'note' ? 1 : step}
                material
                labels={draft.kind === 'note' ? ['Notiz', 'Prüfen'] : undefined}
              />
              {step === 1 ? (
                <>
                  <h2>{titles[draft.kind]}</h2>
                  <p id="context-error" tabIndex={-1} className="wh-field-hint">
                    Diese Angaben gelten für alle Artikel auf deiner Liste.
                  </p>
                </>
              ) : (
                <>
                  <div className="wh-context wh-list-context">
                    <div>
                      <strong>{titles[draft.kind]}</strong>
                      <p>
                        {locationName(draft.locationId)} ·{' '}
                        {snapshot?.projects.find(
                          (p) => p.id === draft.projectId,
                        )?.name ?? 'Ohne Auftrag'}{' '}
                        {draft.projectId}
                      </p>
                    </div>
                    {draft.kind !== 'note' && (
                      <button
                        className="wh-back"
                        aria-label="Lager oder Auftrag ändern"
                        onClick={() => setStep(1)}
                      >
                        Ändern
                      </button>
                    )}
                  </div>
                  <h2>
                    {adding === 'article'
                      ? 'Welchen Artikel möchtest du hinzufügen?'
                      : adding === 'device'
                        ? damage
                          ? 'Schaden / Prüfung melden'
                          : 'Gerät erfassen'
                        : metadata
                          ? titles[draft.kind]
                          : 'Deine Materialliste'}
                  </h2>
                  <p className="wh-field-hint">
                    {adding === 'article'
                      ? 'Scanne den Aufkleber oder suche nach dem Artikelnamen.'
                      : adding === 'device'
                        ? 'Wähle das tatsächliche Gerät. Die Seriennummer steht auf seinem Typenschild.'
                        : metadata
                          ? 'Angaben am ausgewählten Gerät prüfen.'
                          : 'Noch nichts gebucht. Füge Material hinzu und prüfe danach die Liste.'}
                  </p>
                </>
              )}
              {step === 1 ? (
                <>
                  <label>
                    Lagerort
                    <select
                      aria-label="Lagerort"
                      disabled={busy || draft.kind === 'correction'}
                      value={draft.locationId}
                      onChange={(e) =>
                        void edit({ locationId: e.target.value })
                      }
                    >
                      {snapshot?.locations.map((l) => (
                        <option key={l.id} value={l.id}>
                          {l.name}
                        </option>
                      ))}
                    </select>
                  </label>
                  {['issue', 'return', 'legacy_return'].includes(
                    draft.kind,
                  ) && (
                    <WarehouseProjectPicker
                      projects={snapshot?.projects ?? []}
                      value={draft.projectId ?? ''}
                      optional={false}
                      allowWithoutProject={false}
                      disabled={busy}
                      onChange={(value) =>
                        void edit({
                          projectId: value || null,
                          sourceDocument: undefined,
                        })
                      }
                    />
                  )}
                  {[
                    'release',
                    'serial',
                    'correction',
                    'legacy_return',
                    'opening_units',
                  ].includes(draft.kind) && (
                    <label>
                      Begründung
                      <input
                        maxLength={500}
                        value={draft.reason}
                        onChange={(e) => void edit({ reason: e.target.value })}
                      />
                    </label>
                  )}
                  {checked && contextError(draft) && (
                    <p className="wh-sim-alert">{contextError(draft)}</p>
                  )}
                  <button
                    className="wh-primary wh-wide"
                    disabled={busy}
                    onClick={() => {
                      setChecked(true);
                      const e = contextError(draft);
                      if (e) setError(e);
                      else {
                        setStep(2);
                        setError('');
                      }
                    }}
                  >
                    Weiter zur Materialliste
                  </button>
                  <details>
                    <summary>Liste verwerfen</summary>
                    <p>Nur diesen noch ungebuchten Entwurf verwerfen?</p>
                    <button
                      onClick={() =>
                        void save({
                          ...ref.current,
                          draft: null,
                          capture: null,
                        })
                          .then(() => setView('home'))
                          .catch(() => undefined)
                      }
                    >
                      Entwurf endgültig verwerfen
                    </button>
                  </details>
                </>
              ) : (
                <>
                  {adding !== 'closed' &&
                    !metadata &&
                    draft.kind !== 'correction' && (
                      <div className="wh-add-item">
                        <button
                          className="wh-back"
                          onClick={() => {
                            setAdding('closed');
                            setQuery('');
                            setError('');
                          }}
                        >
                          ← Zur Liste
                        </button>
                        {!damage &&
                          ['issue', 'return'].includes(draft.kind) && (
                            <>
                              <p className="wh-field-hint">
                                {selectionResults
                                  ? 'Online-Ergebnis: höchstens 50 Treffer; davon bis zu 10 passende Geräte angezeigt.'
                                  : 'Nur gespeicherte Geräte. Weitere Geräte können fehlen.'}
                              </p>
                              <button
                                disabled={
                                  busy ||
                                  !online.current ||
                                  (!query.trim() && adding !== 'device')
                                }
                                onClick={() => void searchDevices(true)}
                              >
                                Geräte online suchen
                              </button>
                            </>
                          )}
                        {adding === 'device' && (
                          <div className="wh-context">
                            <strong>
                              {
                                snapshot?.articles.find(
                                  (a) => a.id === articleId,
                                )?.name
                              }
                            </strong>
                            <span>
                              Artikelnr.{' '}
                              {article(articleId)?.number ?? articleId}
                            </span>
                          </div>
                        )}
                        {!damage && (
                          <label className="wh-camera wh-scan-primary">
                            {adding === 'device'
                              ? 'Seriennummer fotografieren'
                              : 'Code fotografieren'}
                            <input
                              type="file"
                              accept="image/*"
                              capture="environment"
                              disabled={busy}
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                e.target.value = '';
                                if (f) void camera(f);
                              }}
                            />
                          </label>
                        )}
                        {damage ? (
                          <>
                            <label>
                              Zustand
                              <select
                                aria-label="Zustand melden"
                                value={condition}
                                onChange={(e) =>
                                  updateCapture({ condition: e.target.value })
                                }
                              >
                                <option value="damaged">Beschädigt</option>
                                <option value="inspection_required">
                                  Prüfung nötig
                                </option>
                              </select>
                            </label>
                            <label>
                              Notiz
                              <textarea
                                aria-label="Notiz"
                                rows={3}
                                autoComplete="off"
                                value={note}
                                maxLength={500}
                                onChange={(e) =>
                                  updateCapture({ note: e.target.value })
                                }
                              />
                            </label>
                            <p>
                              Dieses Gerät wird aus deiner Ausgabeliste
                              genommen. Die übrige Liste bleibt erhalten.
                            </p>
                            <button
                              className="wh-primary wh-wide"
                              onClick={() => void reportDamage()}
                            >
                              Zustand melden
                            </button>
                          </>
                        ) : adding === 'article' ? (
                          <>
                            <label>
                              Artikel oder Seriennummer suchen
                              <input
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                autoComplete="off"
                                placeholder="Artikelname, Artikelnummer oder Seriennummer"
                                value={query}
                                onChange={(e) => {
                                  setQuery(e.target.value);
                                  setSelectionResults(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (
                                      articleCode(query.trim()) ||
                                      snapshot?.devices.some(
                                        (d) =>
                                          d.serial === query.trim() ||
                                          d.label === query.trim(),
                                      )
                                    )
                                      void scan(query);
                                  }
                                }}
                              />
                            </label>
                            {matchingArticles.length > 10 && (
                              <p className="wh-field-hint" role="status">
                                {matchingArticles.length} Artikel gefunden · die
                                ersten 10 werden angezeigt. Namen oder
                                Artikelnummer genauer eingeben oder Code
                                scannen.
                              </p>
                            )}
                            {matchingArticles.slice(0, 10).map((a) => (
                              <button
                                className="wh-hit"
                                key={a.id}
                                onClick={() => void chooseArticle(a.id)}
                              >
                                <strong>{a.name}</strong>
                                <span className="wh-field-hint">
                                  Artikelnr. {a.number} ·{' '}
                                  {a.tracked || a.canOpenUnits
                                    ? 'Gerät auswählen'
                                    : 'Menge eingeben'}
                                </span>
                              </button>
                            ))}
                            {query &&
                              ![
                                'receipt',
                                'legacy_return',
                                'opening_units',
                              ].includes(draft.kind) &&
                              (selectionResults ?? snapshot?.devices ?? [])
                                .filter(eligible)
                                .filter((d) =>
                                  simMatches(
                                    [d.serial, d.label].join(' '),
                                    query,
                                  ),
                                )
                                .sort(rankDevices)
                                .slice(0, 10)
                                .map((d) => (
                                  <button
                                    className="wh-hit"
                                    key={d.id}
                                    onClick={() => void addExisting(d)}
                                  >
                                    <strong>
                                      {
                                        snapshot?.articles.find(
                                          (a) => a.id === d.articleId,
                                        )?.name
                                      }
                                    </strong>
                                    <span>
                                      {d.serial
                                        ? 'Seriennummer'
                                        : 'Vorläufige Kennzeichnung'}{' '}
                                      {d.serial ?? d.label}
                                    </span>
                                  </button>
                                ))}
                            <p className="wh-no-results wh-notice">
                              Kein passender Artikel gefunden. Prüfe die
                              Schreibweise oder scanne den Aufkleber.
                            </p>
                          </>
                        ) : [
                            'receipt',
                            'legacy_return',
                            'opening_units',
                          ].includes(draft.kind) ? (
                          <>
                            <label className="wh-check">
                              <input
                                type="checkbox"
                                checked={missing}
                                onChange={(e) => {
                                  updateCapture({
                                    missing: e.target.checked,
                                    physical: false,
                                  });
                                }}
                              />{' '}
                              Seriennummer fehlt oder ist unlesbar
                            </label>
                            {missing ? (
                              <p>
                                Diese Kennzeichnung am Gerät anbringen:{' '}
                                <strong>{label}</strong>. Gerät bleibt gesperrt.
                              </p>
                            ) : (
                              <label>
                                Seriennummer am Gerät
                                <input
                                  autoCapitalize="none"
                                  autoCorrect="off"
                                  spellCheck={false}
                                  autoComplete="off"
                                  maxLength={120}
                                  value={serial}
                                  onChange={(e) => {
                                    updateCapture({
                                      serial: e.target.value,
                                      physical: false,
                                    });
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.preventDefault();
                                  }}
                                />
                              </label>
                            )}
                            <label>
                              Zustand
                              <select
                                aria-label="Zustand"
                                disabled={missing}
                                value={
                                  missing ? 'inspection_required' : condition
                                }
                                onChange={(e) =>
                                  updateCapture({ condition: e.target.value })
                                }
                              >
                                {Object.entries(conditions).map(([v, l]) => (
                                  <option key={v} value={v}>
                                    {l}
                                  </option>
                                ))}
                              </select>
                            </label>
                            <label>
                              Notiz
                              <textarea
                                aria-label="Notiz"
                                rows={3}
                                autoComplete="off"
                                maxLength={500}
                                value={note}
                                onChange={(e) =>
                                  updateCapture({ note: e.target.value })
                                }
                              />
                            </label>
                            <label className="wh-check">
                              <input
                                type="checkbox"
                                checked={physical}
                                onChange={(e) =>
                                  updateCapture({ physical: e.target.checked })
                                }
                              />{' '}
                              {missing
                                ? 'Vorläufige Kennzeichnung ist am Gerät angebracht'
                                : 'Seriennummer stimmt mit dem tatsächlichen Gerät überein'}
                            </label>
                            <button
                              disabled={busy}
                              onClick={() => void addNew()}
                            >
                              Gerät zur Liste hinzufügen
                            </button>

                            <p role="status" className="wh-field-hint">
                              {draft.devices.length} Gerät/Geräte auf der Liste.
                            </p>
                            <button
                              className="wh-primary wh-wide"
                              onClick={() => setAdding('closed')}
                            >
                              Zur Materialliste
                            </button>
                          </>
                        ) : (
                          <>
                            <label>
                              Seriennummer eingeben
                              <input
                                autoCapitalize="none"
                                autoCorrect="off"
                                spellCheck={false}
                                autoComplete="off"
                                placeholder="Seriennummer vom Typenschild"
                                value={query}
                                onChange={(e) => {
                                  setQuery(e.target.value);
                                  setSelectionResults(null);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    if (
                                      articleCode(query.trim()) ||
                                      snapshot?.devices.some(
                                        (d) =>
                                          d.serial === query.trim() ||
                                          d.label === query.trim(),
                                      )
                                    )
                                      void scan(query);
                                  }
                                }}
                              />
                            </label>
                            <p className="wh-field-hint">
                              Gespeicherte Geräte · höchstens 10 Treffer. Suche
                              gegebenenfalls genauer.
                            </p>
                            {(selectionResults ?? snapshot?.devices ?? [])
                              .filter(eligible)
                              .filter(
                                (d) =>
                                  d.articleId === articleId &&
                                  !draft.devices.some((x) => x.id === d.id),
                              )
                              .filter(
                                (d) =>
                                  !query ||
                                  selectionResults !== null ||
                                  simMatches(d.serial ?? d.label ?? '', query),
                              )
                              .sort(rankDevices)
                              .slice(0, 10)
                              .map((d) => (
                                <button
                                  className="wh-hit"
                                  key={d.id}
                                  onClick={() => void addExisting(d)}
                                >
                                  <strong>
                                    {d.serial
                                      ? 'Seriennummer'
                                      : 'Vorläufige Kennzeichnung'}{' '}
                                    {d.serial ?? d.label}
                                  </strong>
                                  <span className="wh-field-hint">
                                    {conditions[d.condition]} · Dieses Gerät
                                    hinzufügen
                                  </span>
                                </button>
                              ))}
                            {!(
                              selectionResults ??
                              snapshot?.devices ??
                              []
                            )?.some(
                              (d) =>
                                eligible(d) &&
                                d.articleId === articleId &&
                                !draft.devices.some((x) => x.id === d.id) &&
                                (selectionResults !== null ||
                                  simMatches(d.serial ?? d.label ?? '', query)),
                            ) && (
                              <p className="wh-notice">
                                Keine passende Seriennummer gefunden.
                                Schreibweise, Lagerort und Auftrag prüfen.
                              </p>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  <div hidden={adding !== 'closed'}>
                    {draft.kind === 'issue' &&
                      draft.projectId &&
                      snapshot?.projectDocuments && (
                        <WarehouseDocumentPicker
                          key={draft.projectId}
                          documents={
                            snapshot.projectDocuments[draft.projectId]
                              ?.documents ?? []
                          }
                        />
                      )}
                    {captureStarted(data.capture) && (
                      <aside className="wh-notice">
                        <strong>
                          {data.capture?.kind === 'damage'
                            ? 'Schadensmeldung noch nicht abgeschickt'
                            : '1 Geräteerfassung noch nicht übernommen'}
                        </strong>
                        <button
                          id="capture-resume"
                          onClick={() =>
                            data.capture && showCapture(data.capture)
                          }
                        >
                          Erfassung fortsetzen
                        </button>
                        <details>
                          <summary>Erfassung verwerfen</summary>
                          <p>
                            Nur diese noch nicht übernommene Eingabe löschen?
                          </p>
                          <button
                            onClick={() =>
                              void save((current) => ({
                                ...current,
                                capture: null,
                              })).catch(() => undefined)
                            }
                          >
                            Erfassung endgültig verwerfen
                          </button>
                        </details>
                      </aside>
                    )}
                    {unresolvedDamage.map((q) => (
                      <aside className="wh-notice" key={q.operation.requestId}>
                        Schadensmeldung {q.operation.devices[0]?.serial}:{' '}
                        {q.state === 'rejected'
                          ? 'abgelehnt – Grund unter Buchungen prüfen.'
                          : 'wartet auf Bestätigung.'}{' '}
                        Gerät getrennt abstellen und nicht ausgeben.
                        <button onClick={() => setView('history')}>
                          Meldung ansehen
                        </button>
                      </aside>
                    ))}
                    {draft.devices.map((d) => (
                      <article
                        className="wh-line"
                        key={d.id}
                        id={'device-' + d.id}
                        tabIndex={-1}
                      >
                        <strong>
                          {
                            snapshot?.articles.find((a) => a.id === d.articleId)
                              ?.name
                          }
                        </strong>
                        <p>
                          {d.serial
                            ? 'Seriennummer'
                            : 'Vorläufige Kennzeichnung'}
                          : <strong>{d.serial ?? d.label}</strong>
                        </p>
                        <p className="wh-field-hint">
                          {conditions[d.condition]}
                        </p>
                        {draft.kind === 'serial' && (
                          <label>
                            Richtige Seriennummer
                            <input
                              autoCapitalize="none"
                              autoCorrect="off"
                              spellCheck={false}
                              autoComplete="off"
                              value={d.serial ?? ''}
                              maxLength={120}
                              onChange={(e) =>
                                void edit({
                                  devices: draft.devices.map((x) =>
                                    x.id === d.id
                                      ? {
                                          ...x,
                                          serial: e.target.value,
                                          physicalConfirmed: false,
                                        }
                                      : x,
                                  ),
                                })
                              }
                            />
                          </label>
                        )}
                        {['return', 'block'].includes(draft.kind) && (
                          <label>
                            Zustand bei Erfassung
                            <select
                              value={d.condition}
                              onChange={(e) =>
                                void edit({
                                  devices: draft.devices.map((x) =>
                                    x.id === d.id
                                      ? { ...x, condition: e.target.value }
                                      : x,
                                  ),
                                })
                              }
                            >
                              {Object.entries(conditions).map(([v, l]) => (
                                <option key={v} value={v}>
                                  {l}
                                </option>
                              ))}
                            </select>
                          </label>
                        )}
                        {metadata && (
                          <label>
                            Notiz
                            <textarea
                              aria-label="Notiz"
                              rows={3}
                              autoComplete="off"
                              value={d.note}
                              maxLength={500}
                              onChange={(e) =>
                                void edit({
                                  devices: draft.devices.map((x) =>
                                    x.id === d.id
                                      ? { ...x, note: e.target.value }
                                      : x,
                                  ),
                                })
                              }
                            />
                          </label>
                        )}
                        {deviceProblem(d) && (
                          <p className="wh-sim-alert">{deviceProblem(d)}</p>
                        )}
                        {checked &&
                          (!metadata || draft.kind === 'serial') &&
                          !d.physicalConfirmed && (
                            <p className="wh-sim-alert">
                              {d.serial ?? d.label}: Kennzeichnung am
                              tatsächlichen Gerät noch abgleichen.
                            </p>
                          )}
                        {draft.kind === 'issue' && (
                          <button onClick={() => void beginDamage(d)}>
                            Schaden / Prüfung melden
                          </button>
                        )}
                        {(!metadata || draft.kind === 'serial') && (
                          <label className="wh-check">
                            <input
                              type="checkbox"
                              checked={d.physicalConfirmed}
                              onChange={(e) =>
                                void edit({
                                  devices: draft.devices.map((x) =>
                                    x.id === d.id
                                      ? {
                                          ...x,
                                          physicalConfirmed: e.target.checked,
                                        }
                                      : x,
                                  ),
                                })
                              }
                            />{' '}
                            Kennzeichnung am tatsächlichen Gerät abgeglichen
                          </label>
                        )}
                        {!metadata && (
                          <button
                            onClick={() =>
                              void edit({
                                devices: draft.devices.filter(
                                  (x) => x.id !== d.id,
                                ),
                              })
                            }
                          >
                            Gerät aus Liste entfernen
                          </button>
                        )}
                      </article>
                    ))}
                    {['receipt', 'issue', 'return'].includes(draft.kind) &&
                      draft.lines
                        .filter(
                          (l) =>
                            !article(l.articleId)?.tracked &&
                            !article(l.articleId)?.canOpenUnits,
                        )
                        .map((line) => {
                          const a = article(line.articleId);
                          const stock = simulationStock(
                            data,
                            line.articleId,
                            draft.locationId,
                          );
                          return (
                            <article className="wh-line" key={line.articleId}>
                              <strong>{a?.name ?? line.articleId}</strong>
                              <p className="wh-field-hint">
                                Artikelnr. {a?.number ?? line.articleId} ·
                                Mengenartikel
                              </p>
                              <WarehouseQuantity
                                id={'device-quantity-' + line.articleId}
                                name={a?.name ?? line.articleId}
                                unit={a?.unit ?? ''}
                                fractional={a?.fractional ?? false}
                                allowEmptyIncrement
                                value={line.quantity}
                                disabled={busy}
                                onChange={(quantity) =>
                                  void edit({
                                    lines: draft.lines.map((l) =>
                                      l.articleId === line.articleId
                                        ? { ...l, quantity }
                                        : l,
                                    ),
                                  })
                                }
                              />
                              {draft.kind === 'issue' && (
                                <p className="wh-field-hint">
                                  {stock == null
                                    ? 'Bestand unbekannt'
                                    : `Verfügbar am gewählten Lagerort: ${Math.max(0, stock)} ${a?.unit ?? ''}`}{' '}
                                  · Gespeicherter Stand{' '}
                                  {data.snapshotAt
                                    ? new Date(data.snapshotAt).toLocaleString(
                                        'de-DE',
                                      )
                                    : 'unbekannt'}{' '}
                                  (keine Reservierung)
                                </p>
                              )}
                              <p
                                id={'hint-device-quantity-' + line.articleId}
                                className={
                                  checked && quantityProblem()
                                    ? 'wh-sim-alert'
                                    : 'wh-field-hint'
                                }
                              >
                                {checked && quantityProblem()
                                  ? quantityProblem()
                                  : 'Nur tatsächlich bereitgestellte Mengen eintragen.'}
                              </p>
                              {draft.kind === 'issue' && (
                                <button
                                  disabled={!online.current}
                                  onClick={() =>
                                    void refresh().catch(() =>
                                      setError(
                                        'Bestand konnte nicht aktualisiert werden.',
                                      ),
                                    )
                                  }
                                >
                                  Bestand aktualisieren
                                </button>
                              )}
                              <button
                                className="wh-back"
                                onClick={() =>
                                  void edit({
                                    lines: draft.lines.filter(
                                      (l) => l.articleId !== line.articleId,
                                    ),
                                  })
                                }
                              >
                                Artikel aus Liste entfernen
                              </button>
                            </article>
                          );
                        })}
                  </div>
                  {adding === 'closed' &&
                    !metadata &&
                    draft.kind !== 'correction' && (
                      <>
                        {!draft.devices.length && !draft.lines.length && (
                          <div className="wh-empty-list">
                            <strong>Die Liste ist noch leer</strong>
                            <p>Beginne mit dem ersten Artikel.</p>
                          </div>
                        )}
                        <button
                          className={
                            draft.devices.length || draft.lines.length
                              ? 'wh-wide'
                              : 'wh-primary wh-wide'
                          }
                          onClick={() => {
                            setAdding('article');
                            setSelectionResults(null);
                            setQuery('');
                            setError('');
                          }}
                        >
                          {' '}
                          {draft.devices.length || draft.lines.length
                            ? 'Weiteren Artikel hinzufügen'
                            : 'Artikel hinzufügen'}{' '}
                        </button>
                      </>
                    )}
                  {adding === 'closed' &&
                    (metadata ||
                      draft.devices.length > 0 ||
                      draft.lines.length > 0) && (
                      <div className="wh-actions wh-draft-actions">
                        <p>
                          {positionCount}{' '}
                          {positionCount === 1
                            ? 'Artikelposition'
                            : 'Artikelpositionen'}{' '}
                          · {draft.devices.length}{' '}
                          {draft.devices.length === 1 ? 'Gerät' : 'Geräte'}
                        </p>
                        <button
                          className="wh-primary wh-wide"
                          disabled={
                            busy ||
                            (!metadata &&
                              !draft.devices.length &&
                              !draft.lines.length)
                          }
                          onClick={() => void review()}
                        >
                          Weiter zum Prüfen
                        </button>
                      </div>
                    )}
                </>
              )}
            </section>
          )}
          {draft && draft.kind !== 'release' && view === 'review' && (
            <WarehouseDeviceReview
              draft={draft}
              snapshot={snapshot}
              ledger={ledger}
              titles={titles}
              conditions={conditions}
              offline={offline}
              connected={connected}
              busy={busy}
              onEdit={() => {
                setView('draft');
                setStep(2);
              }}
              onSubmit={() => void submit()}
            />
          )}
          {view === 'devices' && (
            <section>
              <h2>Bestand</h2>
              <label>
                Seriennummer, Artikel, Projekt oder Lagerort
                <input
                  id="wh-stock-search"
                  enterKeyHint="search"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck={false}
                  autoComplete="off"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setStockArticleId(null);
                    setSearchResults(null);
                  }}
                />
              </label>
              <div className="wh-actions">
                <button
                  disabled={busy || !online.current}
                  onClick={() => void searchDevices()}
                >
                  Online suchen
                </button>
                {(stockArticleId || query || searchResults !== null) && (
                  <button
                    className="wh-back"
                    onClick={() => {
                      setStockArticleId(null);
                      setSearchResults(null);
                      setQuery('');
                      document.getElementById('wh-stock-search')?.focus();
                    }}
                  >
                    {stockArticleId
                      ? 'Alle Artikel anzeigen'
                      : 'Suche zurücksetzen'}
                  </button>
                )}
              </div>
              {stockArticles.length > stockLimit && (
                <p className="wh-field-hint">
                  {stockArticles.length} Artikel · zunächst {stockLimit}{' '}
                  angezeigt. Suche eingrenzen oder weitere anzeigen.
                </p>
              )}
              {stockArticles.slice(0, stockLimit).map((a) => (
                <WarehouseStockArticleCard
                  key={a.id}
                  article={a}
                  stocks={stocksByArticle.get(a.id) ?? []}
                  locationName={locationName}
                  status={heroByArticle.get(a.id)?.status}
                  syncHero={snapshot?.canManage ? api.syncHero : undefined}
                  disabled={busy || !online.current}
                  onDevices={() => {
                    setStockArticleId(a.id);
                    setQuery('');
                    setSearchResults(null);
                    requestAnimationFrame(() =>
                      document.getElementById('wh-stock-devices')?.focus(),
                    );
                  }}
                />
              ))}
              <h3 id="wh-stock-devices" tabIndex={-1}>
                Einzelgeräte
                {stockArticleId
                  ? ` · ${article(stockArticleId)?.name ?? stockArticleId}`
                  : ''}
              </h3>
              <p className="wh-field-hint">
                {searchResults !== null
                  ? 'Online-Ergebnis · höchstens 50 Treffer. Bei 50 Treffern Suche genauer eingrenzen.'
                  : `Gespeicherter Bestand: ${snapshot?.devices.length ?? 0} von ${snapshot?.deviceCount ?? 0} Geräten. Weitere Geräte können fehlen.`}
              </p>
              {stockDevices.length === 0 && (
                <p className="wh-notice">Keine Treffer für diese Suche.</p>
              )}
              {stockDevices.length > stockLimit && (
                <p className="wh-field-hint">
                  {stockDevices.length} passende Geräte · zunächst {stockLimit}{' '}
                  angezeigt.
                </p>
              )}
              {stockDevices.slice(0, stockLimit).map((d) => (
                <WarehouseDeviceCard
                  key={d.id}
                  d={d}
                  articleName={article(d.articleId)?.name ?? d.articleId}
                  locationLabel={locationName(d.locationId)}
                  projectLabel={projectsById.get(d.projectId ?? '')?.name}
                  blocked={localBlock(d.id)}
                  canManage={snapshot?.canManage ?? false}
                  conditions={conditions}
                  titles={titles}
                  events={historyTitle === d.id ? events : null}
                  onBlocked={() => setView('history')}
                  onAction={start}
                  onHistory={() => void history(d)}
                  onCloseHistory={() => setHistoryTitle('')}
                />
              ))}
              {(stockArticles.length > stockLimit ||
                stockDevices.length > stockLimit) && (
                <button onClick={() => setStockLimit((n) => n + 20)}>
                  Weitere Bestände anzeigen
                </button>
              )}
            </section>
          )}
          {view === 'history' && (
            <section>
              <h2>Buchungen</h2>
              <button
                disabled={busy || offline || !connected}
                onClick={() => void sync()}
              >
                Übertragung prüfen
              </button>
              {[...ledger]
                .sort(
                  (a, b) =>
                    Number(b.operation.requestId === data.currentRequestId) -
                      Number(a.operation.requestId === data.currentRequestId) ||
                    Number(
                      ['pending', 'unknown', 'rejected'].includes(b.state),
                    ) -
                      Number(
                        ['pending', 'unknown', 'rejected'].includes(a.state),
                      ),
                )
                .slice(0, ledgerLimit)
                .map((q) => (
                  <article
                    className={
                      'wh-line wh-booking-card ' +
                      (q.operation.requestId === data.currentRequestId
                        ? 'wh-current-result'
                        : '')
                    }
                    key={q.operation.requestId}
                  >
                    {q.operation.requestId === data.currentRequestId && (
                      <p className="wh-field-hint">Dein aktueller Vorgang</p>
                    )}
                    <h3>{titles[q.operation.kind]}</h3>
                    <strong>
                      {q.state === 'confirmed'
                        ? q.operation.kind === 'issue'
                          ? 'Ausgabe gebucht'
                          : `${titles[q.operation.kind]} bestätigt`
                        : q.state === 'stopped'
                          ? 'Automatische Buchung gestoppt · nichts übertragen'
                          : q.state === 'pending' && q.attempted === false
                            ? online.current
                              ? 'Wartet auf Übertragung'
                              : 'Wartet auf Verbindung. Wird anschließend automatisch gebucht.'
                            : q.state === 'rejected'
                              ? 'Nichts gebucht'
                              : q.operation.kind === 'issue'
                                ? 'Bestätigung fehlt. Geräte noch nicht übergeben.'
                                : 'Bestätigung fehlt. Geräte getrennt bereitstellen.'}
                    </strong>
                    {q.state === 'confirmed' && (
                      <p>
                        {q.operation.kind === 'issue' &&
                        q.operation.devices.some((d) => {
                          const current = snapshot?.devices.find(
                            (x) => x.id === d.id,
                          );
                          return (
                            localBlock(d.id) ||
                            (current &&
                              (current.condition !== 'ok' ||
                                current.state !== 'issued' ||
                                current.projectId !== q.operation.projectId))
                          );
                        })
                          ? 'Gerätestatus inzwischen geändert. Bestand prüfen; diese alte Buchung ist keine aktuelle Übergabefreigabe.'
                          : q.result?.message}
                      </p>
                    )}
                    {q.state === 'pending' &&
                      q.operation.devices.length > 0 && (
                        <p>Geräte noch nicht übergeben.</p>
                      )}
                    <p>
                      {locationName(q.operation.locationId)} ·{' '}
                      {
                        snapshot?.projects.find(
                          (p) => p.id === q.operation.projectId,
                        )?.name
                      }{' '}
                      {q.operation.projectId}
                    </p>
                    {q.result?.createdAt && (
                      <p className="wh-field-hint">
                        {q.state === 'confirmed'
                          ? 'Gebucht'
                          : 'Serverentscheidung'}{' '}
                        {new Date(q.result.createdAt).toLocaleString('de-DE')}
                        {q.result.actorId != null &&
                          ` · Benutzer ${q.result.actorId}`}
                      </p>
                    )}
                    {!q.result?.createdAt && q.createdAt && (
                      <p className="wh-field-hint">
                        Erfasst {new Date(q.createdAt).toLocaleString('de-DE')}
                      </p>
                    )}
                    {canStopSimulation(q) && q.operation.kind !== 'block' && (
                      <button
                        onClick={() => void stopQueued(q.operation.requestId)}
                      >
                        Automatische Buchung stoppen
                      </button>
                    )}
                    {q.state === 'stopped' &&
                      !data.queue.some(
                        (x) => x.sourceStoppedId === q.operation.requestId,
                      ) &&
                      data.draftFromStoppedId !== q.operation.requestId && (
                        <button onClick={() => void reopenStopped(q)}>
                          Liste bearbeiten
                        </button>
                      )}
                    <details
                      className="wh-details"
                      open={
                        q.operation.requestId === data.currentRequestId ||
                        q.state !== 'confirmed'
                      }
                    >
                      <summary>
                        <WarehouseIcon kind="info" /> Buchungsdetails ·{' '}
                        {q.operation.lines.length} Artikelpositionen
                      </summary>
                      {q.operation.reason && (
                        <p>Begründung: {q.operation.reason}</p>
                      )}
                      {q.operation.lines.map((line) => (
                        <p key={line.articleId}>
                          {line.quantity} {article(line.articleId)?.unit}{' '}
                          {snapshot?.articles.find(
                            (a) => a.id === line.articleId,
                          )?.name ?? line.articleId}
                        </p>
                      ))}
                      {q.operation.sourceDocument && (
                        <p>
                          Beleg (älterer Entwurf):{' '}
                          {q.operation.sourceDocument.number} · Fassung{' '}
                          {q.operation.sourceDocument.version}
                        </p>
                      )}
                      {q.operation.devices.map((d) => (
                        <p key={d.id}>{d.serial ?? d.label}</p>
                      ))}
                    </details>
                    {q.result?.problems.map((p, i) => (
                      <p className="wh-sim-alert" key={i}>
                        {p.articleId}{' '}
                        {
                          q.operation.devices.find((d) => d.id === p.deviceId)
                            ?.serial
                        }{' '}
                        · {p.message}
                      </p>
                    ))}
                    {q.state === 'rejected' &&
                      !resolvedRequests.has(q.operation.requestId) &&
                      data.queue.some(
                        (own) =>
                          own.operation.requestId === q.operation.requestId,
                      ) && (
                        <button
                          disabled={busy || offline}
                          onClick={() => void repair(q)}
                        >
                          Angaben gezielt berichtigen
                        </button>
                      )}
                    {q.state === 'rejected' &&
                      resolvedRequests.has(q.operation.requestId) && (
                        <p>Durch bestätigte Berichtigung erledigt.</p>
                      )}
                    {q.state === 'confirmed' &&
                      snapshot?.canManage &&
                      ['receipt', 'issue', 'return'].includes(
                        q.operation.kind,
                      ) &&
                      q.operation.devices.length > 0 && (
                        <button onClick={() => void correct(q)}>
                          Gerätebuchung zurücknehmen
                        </button>
                      )}
                  </article>
                ))}
              {ledger.length > ledgerLimit && (
                <button onClick={() => setLedgerLimit((n) => n + 20)}>
                  Weitere Buchungen anzeigen
                </button>
              )}
            </section>
          )}
        </fieldset>
      )}
      {test &&
        (import.meta.env.DEV || import.meta.env.MODE === 'warehouse-test') && (
          <WarehouseDeviceTestPanel
            test={test}
            snapshot={snapshot}
            busy={busy}
            locked={busy || !!draft || pending.length > 0}
            offline={offline}
            lose={lose}
            setOffline={setOffline}
            setLose={setLose}
            refresh={refresh}
            setError={setError}
          />
        )}
    </main>
  );
}
